// services/adminStepUpStorage.ts
//
// The admin step-up token (ORA-864) on this device: a short-lived JWT the
// backend issues after a passkey ceremony, sent as `x-admin-step-up` on every
// request. Kept free of axios and the store so the axios client can read it
// without a circular import; `adminStepUp.ts` re-exports it with the API.
import { jwtDecode } from 'jwt-decode';

export const ADMIN_STEP_UP_STORAGE_KEY = 'adminStepUpToken';
export const ADMIN_STEP_UP_HEADER = 'x-admin-step-up';

/**
 * The admin closed or cancelled a step-up: the browser's passkey sheet, or the
 * step-up prompt itself. Not a failure, so the UI says "Cancelled" rather than
 * showing an error.
 */
export class AdminStepUpCancelledError extends Error {
  constructor(message = 'Admin verification was cancelled') {
    super(message);
    this.name = 'AdminStepUpCancelledError';
  }
}

/** The token's `exp` as a Date, or null when it cannot be decoded. */
const readExpiry = (token: string): Date | null => {
  try {
    const { exp } = jwtDecode<{ exp?: unknown }>(token);
    return typeof exp === 'number' ? new Date(exp * 1000) : null;
  } catch {
    return null;
  }
};

export const clearAdminStepUpToken = (): void => {
  try {
    localStorage.removeItem(ADMIN_STEP_UP_STORAGE_KEY);
  } catch {
    // Storage blocked (private mode, sandboxed frame): nothing to clear.
  }
};

export const storeAdminStepUpToken = (token: string): void => {
  try {
    localStorage.setItem(ADMIN_STEP_UP_STORAGE_KEY, token);
  } catch {
    // Storage blocked: the token lives only as long as this request chain,
    // and the admin is asked again on the next gated call.
  }
};

/**
 * The stored step-up token while it is still valid. An expired or undecodable
 * token is cleared and reported as missing, so it is never sent.
 */
export const getStoredAdminStepUpToken = (): string | null => {
  let token: string | null;
  try {
    token = localStorage.getItem(ADMIN_STEP_UP_STORAGE_KEY);
  } catch {
    return null;
  }

  if (!token) return null;

  const expiry = readExpiry(token);
  if (!expiry || expiry.getTime() <= Date.now()) {
    clearAdminStepUpToken();
    return null;
  }

  return token;
};

/** When this device's admin verification lapses, or null if not verified. */
export const getAdminStepUpExpiry = (): Date | null => {
  const token = getStoredAdminStepUpToken();
  return token ? readExpiry(token) : null;
};
